import { Controller } from '@hotwired/stimulus';
import { getPaywallController } from '../lib/generator_api';
import {
  createBlankConfig,
  normalizeConfig,
  hintsForConfig,
  getTemplate,
  generateCodeGs,
  generatePageHtml,
  headerToKey,
  indexToLetter,
  parseHeaderRow,
  clipboardHeaderPasteText,
  clipboardHeaderColumnCount,
  isStructuredHeaderPaste,
  configForLivePreview,
  generateDeployArtifacts,
  hasEnabledPortalFields,
  syncPortalFields,
  renderPagePreview
} from '../lib/portal_builder';

const COPY_STORAGE_KEY = 'portal_builder_has_copied';
const COPY_CODE_GS_KEY = 'portal_builder_copied_code_gs';
const COPY_PAGE_HTML_KEY = 'portal_builder_copied_page_html';
const HELPER_SIDEBAR_STORAGE_KEY = 'portal_builder_helper_hidden';
const HELPER_MANUAL_STEPS = new Set([
  'apps_script_name',
  'paste_code_gs',
  'make_page_html',
  'paste_page_html',
  'deploy_help'
]);
const PREVIEW_UNAVAILABLE_CODE = '// Preview unavailable';
const PREVIEW_UNAVAILABLE_HTML = '<!-- Preview unavailable';

export default class extends Controller {
  static targets = [
    'templateSelect',
    'sheetName',
    'urlParam',
    'defaultReference',
    'referenceHelp',
    'lookupHeaderSelect',
    'headerSheet',
    'columnList',
    'columnHints',
    'pageTitle',
    'renewalDays',
    'searchEnabled',
    'searchPanel',
    'searchLabel',
    'searchPlaceholder',
    'searchButton',
    'introEnabled',
    'introPanel',
    'introBody',
    'portalFieldsSection',
    'portalFieldsList',
    'portalFieldsHint',
    'notFoundMessage',
    'brandEnabled',
    'brandingPanel',
    'brandLogoUrl',
    'brandCompanyName',
    'brandDescription',
    'brandPrimaryColor',
    'brandBackgroundColor',
    'brandCardColor',
    'brandTextColor',
    'brandPrimaryColorText',
    'brandBackgroundColorText',
    'brandCardColorText',
    'brandTextColorText',
    'codeOutput',
    'htmlOutput',
    'copyCodeBtn',
    'copyHtmlBtn',
    'codeWrap',
    'htmlWrap',
    'deployGuide',
    'deployBackdrop',
    'deployTab',
    'deployPanel',
    'redeployPanel',
    'toast',
    'stepProgress',
    'settingsPanel',
    'sheetPanel',
    'portalSettingsPanel',
    'codeOutputPanel',
    'htmlOutputPanel',
    'htmlOutputTitle',
    'pagePreviewPanel',
    'pagePreviewFrame',
    'pagePreviewWrap',
    'pagePreviewEmpty',
    'deployActions',
    'outputActions',
    'stepNav',
    'stepNavItem',
    'stepNavIndicator',
    'stepNavMeta',
    'helperSidebar',
    'helperSidebarHideBtn',
    'helperSidebarShowBtn'
  ];

  static values = {
    recordUseUrl: String,
    initialTemplate: { type: String, default: '' },
    gated: { type: Boolean, default: false }
  };

  connect() {
    this.restoreNativeSelects();
    this.config = createBlankConfig();
    this.urlParamManual = false;

    try {
      const fromUrl = new URLSearchParams(window.location.search).get('template');
      if (fromUrl) {
        this.loadTemplateByKey(fromUrl, { confirm: false, fillOnly: true });
      } else {
        this.syncFormFromConfig();
      }
    } catch (err) {
      console.error('Portal builder template load failed', err);
      this.config = createBlankConfig();
      this.syncFormFromConfig();
    }

    this.closeDeployGuide();
    this.helperSidebarHidden = false;
    try {
      this.helperSidebarHidden = localStorage.getItem(HELPER_SIDEBAR_STORAGE_KEY) === '1';
    } catch (_) {
      /* ignore */
    }
    this.applyHelperSidebarState();
    this.bindHeaderSheetEvents();
    this.bindLivePreviewEvents();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  disconnect() {
    this.unbindHeaderSheetEvents();
    this.unbindLivePreviewEvents();
  }

  bindLivePreviewEvents() {
    if (this.livePreviewEventsBound) return;
    this.onLivePreviewInput = (event) => {
      if (event.target.closest('[data-portal-builder-target="codeOutput"], [data-portal-builder-target="htmlOutput"]')) {
        return;
      }
      this.refreshLivePreviewNow();
    };
    this.element.addEventListener('input', this.onLivePreviewInput);
    this.element.addEventListener('change', this.onLivePreviewInput);
    this.livePreviewEventsBound = true;
  }

  unbindLivePreviewEvents() {
    if (!this.livePreviewEventsBound) return;
    this.element.removeEventListener('input', this.onLivePreviewInput);
    this.element.removeEventListener('change', this.onLivePreviewInput);
    this.livePreviewEventsBound = false;
  }

  /** Choices.js grays out selects; portal builder needs native dropdowns. */
  restoreNativeSelects() {
    this.element.querySelectorAll('select.form-select').forEach((select) => {
      if (select.choices?.destroy) {
        select.choices.destroy();
        return;
      }
      const wrapper = select.closest('.choices');
      if (!wrapper?.parentNode) return;
      wrapper.parentNode.insertBefore(select, wrapper);
      wrapper.remove();
      select.hidden = false;
      select.removeAttribute('hidden');
      select.style.display = '';
    });
  }

  bindHeaderSheetEvents() {
    if (!this.hasHeaderSheetTarget || this.headerSheetEventsBound) return;
    this.onHeaderSheetInput = (event) => this.onHeaderCellInput(event);
    this.onHeaderSheetClick = (event) => this.onHeaderCellClick(event);
    this.onHeaderSheetPaste = (event) => this.onHeaderPaste(event);
    this.headerSheetTarget.addEventListener('input', this.onHeaderSheetInput, true);
    this.headerSheetTarget.addEventListener('click', this.onHeaderSheetClick);
    // Capture so row paste runs before the browser fills one cell.
    this.headerSheetTarget.addEventListener('paste', this.onHeaderSheetPaste, true);
    this.headerSheetEventsBound = true;
  }

  unbindHeaderSheetEvents() {
    if (!this.hasHeaderSheetTarget || !this.headerSheetEventsBound) return;
    this.headerSheetTarget.removeEventListener('input', this.onHeaderSheetInput, true);
    this.headerSheetTarget.removeEventListener('click', this.onHeaderSheetClick);
    this.headerSheetTarget.removeEventListener('paste', this.onHeaderSheetPaste, true);
    this.headerSheetEventsBound = false;
  }

  paywall() {
    return getPaywallController(this.element);
  }

  requireMembershipExport() {
    if (!this.gatedValue) return true;

    this.paywall()?.open();
    return false;
  }

  loadTemplate(event) {
    const key = event?.target?.value ?? this.templateSelectTarget.value;
    this.loadTemplateByKey(key, { confirm: true, fillOnly: true });
  }

  loadTemplateByKey(key, { confirm, fillOnly = false }) {
    if (
      confirm &&
      this.config.templateKey !== 'blank' &&
      key !== this.config.templateKey &&
      this.config.sheet.columns.length > 0
    ) {
      if (!window.confirm('Switch template? Your column and settings will be replaced.')) {
        this.templateSelectTarget.value = this.config.templateKey || 'blank';
        return;
      }
    }

    if (key === 'blank') {
      this.config = createBlankConfig();
      this.config.templateKey = 'blank';
    } else {
      const t = getTemplate(key);
      if (t) this.config = normalizeConfig(structuredClone(t));
    }

    this.syncFormFromConfig();
    this.toggleLibrarySettings();
    this.refreshLivePreviewNow();
    this.updateStepUi();
    this.closeDeployGuide();
  }

  toggleLibrarySettings() {
    const el = this.element.querySelector('[data-portal-builder-library-settings]');
    if (el) el.classList.toggle('hidden', this.config.templateKey !== 'library');
  }

  syncFormFromConfig() {
    const c = this.config;
    if (this.hasTemplateSelectTarget) this.templateSelectTarget.value = c.templateKey || 'blank';
    if (this.hasSheetNameTarget) this.sheetNameTarget.value = c.sheet.name;
    if (this.hasUrlParamTarget) this.urlParamTarget.value = c.sheet.urlParam;
    if (this.hasDefaultReferenceTarget) {
      const ref = c.sheet.defaultReference === 'letter' ? 'letter' : 'header';
      this.defaultReferenceTarget.value = ref;
    }
    this.updateReferenceHelp();
    if (this.hasPageTitleTarget) this.pageTitleTarget.value = c.script.pageTitle;
    if (this.hasRenewalDaysTarget) this.renewalDaysTarget.value = c.script.renewalDays;
    if (this.hasSearchEnabledTarget) this.searchEnabledTarget.checked = c.html.sections.search?.enabled !== false;
    if (this.hasSearchLabelTarget) this.searchLabelTarget.value = c.html.sections.search?.label || '';
    if (this.hasSearchPlaceholderTarget) {
      this.searchPlaceholderTarget.value = c.html.sections.search?.placeholder || '';
    }
    if (this.hasSearchButtonTarget) this.searchButtonTarget.value = c.html.sections.search?.buttonText || '';
    if (this.hasIntroEnabledTarget) this.introEnabledTarget.checked = Boolean(c.html.sections.intro?.enabled);
    if (this.hasIntroBodyTarget) this.introBodyTarget.value = c.html.sections.intro?.body || '';
    if (this.hasNotFoundMessageTarget) {
      this.notFoundMessageTarget.value = c.html.sections.notFound?.message || '';
    }
    this.syncBrandingFormFromConfig(c.html.sections.branding);
    this.updateBrandingPanelVisibility();
    this.updateSearchPanelVisibility();
    this.updateIntroPanelVisibility();

    this.renderHeaderSheet();
    this.renderLookupHeaderSelect();
    this.renderPortalFieldsList();
  }

  lookupOptionLabel(col) {
    const header = String(col.header || '').trim();
    return `${col.letter}: ${header || '(empty)'}`;
  }

  lookupColumnIndex(config = this.config) {
    const { lookup, columns } = config.sheet;
    if (!columns?.length) return -1;

    if (config === this.config && this.hasLookupHeaderSelectTarget) {
      const val = this.lookupHeaderSelectTarget.value;
      if (val !== '') {
        const fromSelect = parseInt(val, 10);
        if (!Number.isNaN(fromSelect) && columns[fromSelect] !== undefined) return fromSelect;
      }
    }

    const stored = lookup?.columnIndex;
    if (typeof stored === 'number' && stored >= 0 && columns[stored] !== undefined) {
      return stored;
    }

    const header = String(lookup?.header || '').trim();
    if (header) {
      const idx = columns.findIndex((c) => String(c.header || '').trim() === header);
      if (idx >= 0) return idx;
    }
    if (lookup?.letter) {
      const idx = columns.findIndex((c) => c.letter === lookup.letter);
      if (idx >= 0) return idx;
    }
    return -1;
  }

  renderLookupHeaderSelect({ skipDomRead = false } = {}) {
    if (!this.hasLookupHeaderSelectTarget) return;

    if (!skipDomRead) this.readColumnsFromDom();
    const cols = this.config.sheet.columns;
    const prevVal = this.lookupHeaderSelectTarget.value;

    if (cols.length === 0) {
      this.lookupHeaderSelectTarget.innerHTML = '<option value="">Paste headers in row 1 above…</option>';
      this.lookupHeaderSelectTarget.disabled = true;
      return;
    }

    this.lookupHeaderSelectTarget.disabled = false;
    this.lookupHeaderSelectTarget.innerHTML = [
      '<option value="">Choose unique ID column…</option>',
      ...cols.map((c, i) => {
        const label = this.lookupOptionLabel(c);
        return `<option value="${i}">${this.escapeAttr(label)}</option>`;
      })
    ].join('');

    const prevIdx = parseInt(prevVal, 10);
    if (prevVal !== '' && !Number.isNaN(prevIdx) && cols[prevIdx] !== undefined) {
      this.lookupHeaderSelectTarget.value = String(prevIdx);
    } else {
      const selectedIdx = this.lookupColumnIndex();
      if (selectedIdx >= 0) {
        this.lookupHeaderSelectTarget.value = String(selectedIdx);
      }
    }

    this.syncLookupFromSelect();
  }

  renderHeaderSheet() {
    if (!this.hasColumnListTarget) return;
    const cols = this.config.sheet.columns.length
      ? this.config.sheet.columns
      : [{ header: '', letter: 'A', number: 1 }];
    const lookupIdx = this.lookupColumnIndex();

    const letterHeaders = cols
      .map((c) => `<th class="portal-builder__sheet-col-label" scope="col">${c.letter}</th>`)
      .join('');

    const headerCells = cols
      .map((col, i) => {
        const isLookup = i === lookupIdx;
        return `<td class="portal-builder__sheet-td${isLookup ? ' portal-builder__sheet-td--lookup' : ''}">
          <input type="text" class="portal-builder__sheet-cell" data-index="${i}" data-field="header" value="${this.escapeAttr(col.header)}" placeholder="${col.letter}" aria-label="Column ${col.letter} header" title="Click to set as unique ID column">
        </td>`;
      })
      .join('');

    this.columnListTarget.innerHTML = `
      <table class="portal-builder__sheet-table" role="grid" aria-label="Sheet row 1 headers">
        <thead>
          <tr>
            <th class="portal-builder__sheet-gutter" scope="col" aria-hidden="true"></th>
            ${letterHeaders}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th class="portal-builder__sheet-gutter portal-builder__sheet-row-num" scope="row">1</th>
            ${headerCells}
          </tr>
        </tbody>
      </table>`;
  }

  onHeaderCellInput(event) {
    if (!event.target.matches('[data-field="header"]')) return;
    this.refreshFromHeaderRow();
  }

  onHeaderCellClick(event) {
    const input = event.target.closest('[data-field="header"]');
    if (!input) return;
    const i = parseInt(input.dataset.index, 10);
    if (Number.isNaN(i)) return;
    if (this.hasLookupHeaderSelectTarget) {
      this.lookupHeaderSelectTarget.value = String(i);
    }
    this.urlParamManual = false;
    this.syncLookupFromSelect();
    this.syncUrlParamFromLookup({ force: true });
    this.updateLookupHighlightInSheet();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  refreshFromHeaderRow() {
    this.readColumnsFromDom();
    this.renderLookupHeaderSelect({ skipDomRead: true });
    this.syncUrlParamFromLookup();
    this.updateLookupHighlightInSheet();

    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  updateLookupHighlightInSheet() {
    if (!this.hasColumnListTarget) return;
    const idx = this.lookupColumnIndex();
    this.columnListTarget.querySelectorAll('.portal-builder__sheet-td').forEach((td) => {
      const cell = td.querySelector('[data-field="header"]');
      const i = cell ? parseInt(cell.dataset.index, 10) : -1;
      td.classList.toggle('portal-builder__sheet-td--lookup', i === idx);
    });
  }

  onHeaderPaste(event) {
    const inHeaderGrid =
      this.headerSheetTarget?.contains(event.target) ||
      event.target?.closest?.('.portal-builder__sheet-grid, [data-field="header"]');
    if (!inHeaderGrid) return;

    const text = clipboardHeaderPasteText(event.clipboardData);
    let headers = parseHeaderRow(text);
    if (!headers.length) return;

    const htmlColumnCount = clipboardHeaderColumnCount(event.clipboardData);
    const structured = isStructuredHeaderPaste(text, headers) || htmlColumnCount > 1;

    // Browsers often put only the first cell in text/plain; do not defer to native paste
    // when clipboard HTML clearly contains a multi-column row.
    const singleCellPaste =
      event.target?.matches?.('[data-field="header"]') &&
      !structured &&
      headers.length === 1 &&
      htmlColumnCount <= 1;

    if (singleCellPaste) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const ref = this.config.sheet.defaultReference === 'letter' ? 'letter' : 'header';
    this.config.sheet.columns = headers.map((header, i) => ({
      header,
      key: headerToKey(header) || `column${i + 1}`,
      reference: ref,
      letter: indexToLetter(i),
      number: i + 1
    }));

    this.syncSheetColumnsFromConfig();
    this.showToast(`Pasted ${headers.length} column${headers.length === 1 ? '' : 's'}.`);
  }

  /** Push config.sheet.columns to the grid without clobbering from stale DOM. */
  syncSheetColumnsFromConfig() {
    this.renderHeaderSheet();
    this.renderLookupHeaderSelect({ skipDomRead: true });
    this.renderPortalFieldsList();
    this.updateLookupHighlightInSheet();
    this.updateReferenceHelp();
    this.syncLookupFromSelect();
    this.renderHints();

    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  onHeaderSheetFocus() {
    if (this.hasHeaderSheetTarget) {
      this.headerSheetTarget.classList.add('portal-builder__sheet-grid--focused');
    }
  }

  settingsGroupForStep(stepKey) {
    if (stepKey === 'headers') return 'headers';
    if (stepKey === 'lookup') return 'sheet';
    if (stepKey === 'page_title' || stepKey === 'portal_fields') return 'portal';
    return null;
  }

  focusSettingsGroup(groupKey) {
    if (!groupKey) return;
    const el = this.element.querySelector(`[data-settings-group="${groupKey}"]`);
    if (el?.tagName === 'DETAILS') el.open = true;
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  portalSettingsComplete() {
    const c = this.configFromForm();
    return (
      this.sheetStepComplete() &&
      this.scriptStepComplete() &&
      hasEnabledPortalFields(c)
    );
  }

  stepCompletion() {
    const sheetReq = this.sheetStepRequirements();
    const headersDone = Boolean(sheetReq.items.find((i) => i.key === 'headers')?.done);
    const lookupDone = Boolean(sheetReq.items.find((i) => i.key === 'lookup')?.done);
    const c = this.configFromForm();

    let copiedCodeGs = false;
    let copiedPageHtml = false;
    try {
      copiedCodeGs = localStorage.getItem(COPY_CODE_GS_KEY) === '1';
      copiedPageHtml = localStorage.getItem(COPY_PAGE_HTML_KEY) === '1';
    } catch (_) {
      /* ignore */
    }

    return {
      headers: headersDone,
      lookup: lookupDone,
      page_title: headersDone && lookupDone && this.scriptStepComplete(),
      portal_fields: headersDone && lookupDone && hasEnabledPortalFields(c),
      apps_script_name: false,
      copy_code_gs: copiedCodeGs,
      paste_code_gs: false,
      make_page_html: false,
      copy_page_html: copiedPageHtml,
      paste_page_html: false,
      deploy: this.portalSettingsComplete(),
      deploy_help: false
    };
  }

  isHelperStepLocked(stepKey) {
    const done = this.stepCompletion();
    if (stepKey === 'lookup') return !done.headers;
    if (stepKey === 'page_title' || stepKey === 'portal_fields') return !done.lookup;
    if (
      stepKey === 'copy_code_gs' ||
      stepKey === 'copy_page_html' ||
      stepKey === 'deploy'
    ) {
      return !this.portalSettingsComplete();
    }
    return false;
  }

  activeHelperStepKey(done) {
    for (const btn of this.stepNavItemTargets) {
      const key = btn.dataset.step;
      if (HELPER_MANUAL_STEPS.has(key)) continue;
      if (key === 'deploy_help') continue;
      if (!done[key]) return key;
    }
    return 'deploy';
  }

  toggleHelperSidebar() {
    this.helperSidebarHidden = !this.helperSidebarHidden;
    try {
      localStorage.setItem(HELPER_SIDEBAR_STORAGE_KEY, this.helperSidebarHidden ? '1' : '0');
    } catch (_) {
      /* ignore */
    }
    this.applyHelperSidebarState();
  }

  applyHelperSidebarState() {
    const hidden = Boolean(this.helperSidebarHidden);
    this.element.classList.toggle('portal-builder--helper-hidden', hidden);

    if (this.hasHelperSidebarTarget) {
      this.helperSidebarTarget.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }
    if (this.hasHelperSidebarHideBtnTarget) {
      this.helperSidebarHideBtnTarget.setAttribute('aria-expanded', hidden ? 'false' : 'true');
    }
    if (this.hasHelperSidebarShowBtnTarget) {
      this.helperSidebarShowBtnTarget.hidden = !hidden;
    }
  }

  goToHelperStep(event) {
    event?.preventDefault();
    const stepKey = event.params?.step;
    if (!stepKey) return;

    if (this.isHelperStepLocked(stepKey)) {
      this.showToast('Complete the earlier helper steps on this page first.');
      this.updateStepUi();
      return;
    }

    this.scrollToHelperStep(stepKey);
  }

  openDeployGuideFromHelper(event) {
    event?.preventDefault();
    this.openDeployGuide(event);
  }

  scrollToHelperStep(stepKey) {
    const scrollByStep = {
      headers: 'sheet',
      lookup: 'sheet',
      page_title: 'portal',
      portal_fields: 'portal',
      copy_code_gs: 'code',
      paste_code_gs: 'code',
      make_page_html: 'html',
      copy_page_html: 'html',
      paste_page_html: 'html',
      deploy: 'code'
    };

    const settingsGroup = this.settingsGroupForStep(stepKey);
    if (settingsGroup) this.focusSettingsGroup(settingsGroup);

    const scroll = scrollByStep[stepKey];
    const el = scroll ? this.element.querySelector(`[data-helper-scroll="${scroll}"]`) : null;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (stepKey === 'portal_fields' && this.hasPortalFieldsSectionTarget) {
      this.portalFieldsSectionTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    if (stepKey === 'page_title' && this.hasPageTitleTarget) {
      this.pageTitleTarget.focus({ preventScroll: true });
    }
    if (stepKey === 'lookup' && this.hasLookupHeaderSelectTarget) {
      this.lookupHeaderSelectTarget.focus({ preventScroll: true });
    }
  }

  updateStepNav() {
    if (!this.hasStepNavItemTarget) return;

    const done = this.stepCompletion();
    const activeKey = this.activeHelperStepKey(done);

    this.stepNavItemTargets.forEach((btn, index) => {
      const key = btn.dataset.step;
      const isManual = btn.dataset.manual === 'true';
      const isDone = !isManual && Boolean(done[key]);
      const isCurrent = key === activeKey;
      const isLocked = this.isHelperStepLocked(key);

      btn.classList.toggle('portal-builder__step-nav-item--done', isDone);
      btn.classList.toggle('portal-builder__step-nav-item--current', isCurrent);
      btn.classList.toggle('portal-builder__step-nav-item--locked', isLocked);
      if (isCurrent) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');
      btn.disabled = isLocked;

      const indicator = this.stepNavIndicatorTargets.find((el) => el.dataset.stepIndicator === key);
      if (indicator) {
        indicator.classList.toggle('portal-builder__step-nav-indicator--done', isDone);
        indicator.classList.toggle('portal-builder__step-nav-indicator--current', isCurrent);
        if (isManual) {
          indicator.textContent = '·';
        } else if (isDone) {
          indicator.textContent = '✓';
        } else {
          indicator.textContent = String(index + 1);
        }
      }
    });

    this.updateStepNavMeta(done, activeKey);
    this.highlightSettingsGroups(activeKey);
  }

  updateStepNavMeta(done, activeKey) {
    if (!this.hasStepNavMetaTarget) return;

    const sheetReq = this.sheetStepRequirements();
    const headerItem = sheetReq.items.find((i) => i.key === 'headers');
    const lookupItem = sheetReq.items.find((i) => i.key === 'lookup');

    const meta = {
      headers: done.headers
        ? headerItem?.label || 'Done'
        : 'Paste row 1 from Google Sheets',
      lookup: done.lookup
        ? lookupItem?.label || 'Done'
        : done.headers
          ? 'Choose your unique ID column'
          : 'After header row',
      page_title: done.page_title
        ? 'Done'
        : done.lookup
          ? 'Browser tab & heading'
          : 'After ID column',
      portal_fields: done.portal_fields
        ? 'Done'
        : done.page_title
          ? 'Check columns to show'
          : 'After page title',
      copy_code_gs: done.copy_code_gs ? 'Copied' : 'Use the Code.gs panel',
      copy_page_html: done.copy_page_html ? 'Copied' : 'Use the Page.html panel',
      deploy: done.deploy ? 'Ready' : 'After setup is complete',
      deploy_help: 'Step-by-step guide'
    };

    this.stepNavMetaTargets.forEach((el) => {
      const key = el.dataset.stepMeta;
      if (meta[key]) el.textContent = meta[key];
      else if (el.dataset.stepMeta === activeKey) el.textContent = 'Do this step in Apps Script';
    });
  }

  highlightSettingsGroups(activeKey) {
    const currentGroup = this.settingsGroupForStep(activeKey);
    this.element.querySelectorAll('[data-settings-group]').forEach((el) => {
      const group = el.dataset.settingsGroup;
      const isCurrent = group === currentGroup;
      el.classList.toggle('portal-builder__settings-group--current', isCurrent);
      if (isCurrent && el.tagName === 'DETAILS') el.open = true;
      if (isCurrent && (group === 'headers' || group === 'sheet')) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });

    const { items } = this.sheetStepRequirements();
    const needsHeaders = !items.find((i) => i.key === 'headers')?.done;
    const needsLookup = !items.find((i) => i.key === 'lookup')?.done;

    if (this.hasHeaderSheetTarget) {
      this.headerSheetTarget.classList.toggle('portal-builder__sheet-grid--needed', needsHeaders);
    }
    if (this.hasLookupHeaderSelectTarget) {
      this.lookupHeaderSelectTarget.classList.toggle(
        'portal-builder__field--needed',
        needsLookup && !needsHeaders
      );
    }
  }

  updateReferenceHelp() {
    if (!this.hasReferenceHelpTarget || !this.hasDefaultReferenceTarget) return;
    const isLetter = this.defaultReferenceTarget.value === 'letter';
    this.referenceHelpTarget.innerHTML = isLetter
      ? 'Fixed index: <code>row[0]</code> is always column A.'
      : 'Matches header text: <code>headers.indexOf("Status")</code>.';
  }

  escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  addColumn(event) {
    event?.preventDefault();
    this.readColumnsFromDom();
    const i = this.config.sheet.columns.length;
    const ref = this.config.sheet.defaultReference === 'letter' ? 'letter' : 'header';
    this.config.sheet.columns.push({
      header: '',
      key: `column${i + 1}`,
      reference: ref,
      letter: indexToLetter(i),
      number: i + 1
    });
    this.syncSheetColumnsFromConfig();
  }

  removeLastColumn(event) {
    event?.preventDefault();
    this.readColumnsFromDom();
    if (this.config.sheet.columns.length <= 1) return;
    this.config.sheet.columns.pop();
    this.syncSheetColumnsFromConfig();
  }

  onSheetSettingsChange() {
    this.readColumnsFromDom();
    this.syncLookupFromSelect();
    this.syncUrlParamFromLookup();
    this.config = this.configFromForm();
    this.renderLookupHeaderSelect({ skipDomRead: true });
    this.updateLookupHighlightInSheet();
    this.renderHints();
    this.updateReferenceHelp();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  onUrlParamInput() {
    this.urlParamManual = true;
    this.onSheetSettingsChange();
  }

  syncLookupFromSelect() {
    if (!this.hasLookupHeaderSelectTarget) return;
    const val = this.lookupHeaderSelectTarget.value;
    if (val === '') {
      if (this.config.sheet.lookup) {
        this.config.sheet.lookup.columnIndex = -1;
      }
      return;
    }

    const i = parseInt(val, 10);
    const col = this.config.sheet.columns[i];
    if (Number.isNaN(i) || !col) return;

    const header = String(col.header || '').trim();
    this.config.sheet.lookup.header = header;
    this.config.sheet.lookup.letter = col.letter;
    this.config.sheet.lookup.number = col.number;
    this.config.sheet.lookup.columnIndex = i;
    const ref = this.config.sheet.defaultReference === 'letter' ? 'letter' : 'header';
    this.config.sheet.lookup.reference = !header && ref === 'header' ? 'letter' : ref;
  }

  lookupHeaderSelected() {
    this.urlParamManual = false;
    this.readColumnsFromDom();
    this.syncLookupFromSelect();
    this.syncUrlParamFromLookup({ force: true });
    this.config = this.configFromForm();
    this.updateLookupHighlightInSheet();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  syncUrlParamFromLookup({ force = false } = {}) {
    if (!force && this.urlParamManual) return;

    const idx = this.lookupColumnIndex();
    if (idx < 0) return;

    const header = String(this.config.sheet.columns[idx]?.header || '').trim();
    if (!header) return;

    const param = header.toLowerCase().replace(/[^\w]/g, '') || 'id';
    if (this.hasUrlParamTarget) this.urlParamTarget.value = param;
    this.config.sheet.urlParam = param;
  }

  onPortalSettingsChange() {
    this.syncBrandColorTextFields();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  onBrandEnabledChange() {
    this.updateBrandingPanelVisibility();
    this.onPortalSettingsChange();
  }

  onSearchEnabledChange() {
    this.updateSearchPanelVisibility();
    this.onPortalSettingsChange();
  }

  onIntroEnabledChange() {
    this.updateIntroPanelVisibility();
    this.onPortalSettingsChange();
  }

  onPortalFieldChange() {
    this.config = this.configFromForm();
    this.refreshLivePreviewNow();
    this.updateStepUi();
  }

  updateBrandingPanelVisibility() {
    if (!this.hasBrandingPanelTarget) return;
    const show = this.hasBrandEnabledTarget && this.brandEnabledTarget.checked;
    this.brandingPanelTarget.classList.toggle('hidden', !show);
  }

  updateSearchPanelVisibility() {
    if (!this.hasSearchPanelTarget) return;
    const show = this.hasSearchEnabledTarget && this.searchEnabledTarget.checked;
    this.searchPanelTarget.classList.toggle('hidden', !show);
  }

  updateIntroPanelVisibility() {
    if (!this.hasIntroPanelTarget) return;
    const show = this.hasIntroEnabledTarget && this.introEnabledTarget.checked;
    this.introPanelTarget.classList.toggle('hidden', !show);
  }

  renderPortalFieldsList() {
    if (!this.hasPortalFieldsListTarget) return;

    syncPortalFields(this.config);
    const fields = this.config.html?.sections?.portalFields || [];
    const lookupIdx = this.lookupColumnIndex();

    if (this.hasPortalFieldsHintTarget) {
      if (!fields.length) {
        this.portalFieldsHintTarget.textContent = 'Paste headers first';
      } else {
        const enabled = fields.filter((f) => f.enabled).length;
        this.portalFieldsHintTarget.textContent = `${enabled} of ${fields.length} shown`;
      }
    }

    if (!fields.length) {
      this.portalFieldsListTarget.innerHTML =
        '<p class="portal-builder__portal-fields-empty text-[11px] text-slate-500 px-1 py-2 mb-0">Add column headers above to choose what appears on the portal.</p>';
      return;
    }

    this.portalFieldsListTarget.innerHTML = fields
      .map((field, index) => {
        const isLookup = lookupIdx >= 0 && this.config.sheet.columns[lookupIdx]?.key === field.columnKey;
        const lookupNote = isLookup ? ' <span class="text-slate-400">(ID column)</span>' : '';
        const inputId = `pb-portal-field-${index}`;
        return `<label class="portal-builder__portal-field-row" for="${inputId}">
          <input type="checkbox" id="${inputId}" class="portal-builder__portal-field-check" data-portal-field-key="${this.escapeAttr(field.columnKey)}" data-action="change->portal-builder#onPortalFieldChange" ${field.enabled ? 'checked' : ''}>
          <span class="portal-builder__portal-field-label">${this.escapeHtml(field.label)}${lookupNote}</span>
        </label>`;
      })
      .join('');
  }

  applyPortalFieldsFromDom(config) {
    if (!this.hasPortalFieldsListTarget) return;
    const toggles = this.portalFieldsListTarget.querySelectorAll('[data-portal-field-key]');
    if (!toggles.length) return;

    const byKey = new Map(
      (config.html?.sections?.portalFields || []).map((item) => [item.columnKey, { ...item }])
    );
    toggles.forEach((input) => {
      const key = input.getAttribute('data-portal-field-key');
      if (!key || !byKey.has(key)) return;
      byKey.get(key).enabled = input.checked;
    });
    config.html.sections.portalFields = [...byKey.values()];
  }

  onBrandColorTextInput(event) {
    const param = event.params?.color;
    const hex = event.target.value.trim();
    if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) return;
    if (param === 'primary' && this.hasBrandPrimaryColorTarget) {
      this.brandPrimaryColorTarget.value = hex;
    } else if (param === 'background' && this.hasBrandBackgroundColorTarget) {
      this.brandBackgroundColorTarget.value = hex;
    } else if (param === 'card' && this.hasBrandCardColorTarget) {
      this.brandCardColorTarget.value = hex;
    } else if (param === 'text' && this.hasBrandTextColorTarget) {
      this.brandTextColorTarget.value = hex;
    }
    this.onPortalSettingsChange();
  }

  syncBrandingFormFromConfig(branding = {}) {
    const b = branding || {};
    if (this.hasBrandEnabledTarget) this.brandEnabledTarget.checked = Boolean(b.enabled);
    if (this.hasBrandLogoUrlTarget) this.brandLogoUrlTarget.value = b.logoUrl || '';
    if (this.hasBrandCompanyNameTarget) this.brandCompanyNameTarget.value = b.companyName || '';
    if (this.hasBrandDescriptionTarget) this.brandDescriptionTarget.value = b.description || '';
    if (this.hasBrandPrimaryColorTarget) this.brandPrimaryColorTarget.value = b.primaryColor || '#2563eb';
    if (this.hasBrandBackgroundColorTarget) {
      this.brandBackgroundColorTarget.value = b.backgroundColor || '#ffffff';
    }
    if (this.hasBrandCardColorTarget) this.brandCardColorTarget.value = b.cardColor || '#e6e6e6';
    if (this.hasBrandTextColorTarget) this.brandTextColorTarget.value = b.textColor || '#334155';
    this.syncBrandColorTextFields();
  }

  syncBrandColorTextFields() {
    if (this.hasBrandPrimaryColorTarget && this.hasBrandPrimaryColorTextTarget) {
      this.brandPrimaryColorTextTarget.value = this.brandPrimaryColorTarget.value;
    }
    if (this.hasBrandBackgroundColorTarget && this.hasBrandBackgroundColorTextTarget) {
      this.brandBackgroundColorTextTarget.value = this.brandBackgroundColorTarget.value;
    }
    if (this.hasBrandCardColorTarget && this.hasBrandCardColorTextTarget) {
      this.brandCardColorTextTarget.value = this.brandCardColorTarget.value;
    }
    if (this.hasBrandTextColorTarget && this.hasBrandTextColorTextTarget) {
      this.brandTextColorTextTarget.value = this.brandTextColorTarget.value;
    }
  }

  hasRealColumns() {
    return this.config?.sheet?.columns?.length > 0;
  }

  refreshLivePreviewNow() {
    this.refreshLivePreview();
  }

  getDeployOutputs() {
    if (!this.portalSettingsComplete()) return null;
    try {
      return generateDeployArtifacts(this.configFromForm());
    } catch (err) {
      console.error('Portal builder deploy output failed', err);
      return null;
    }
  }

  hasCopyablePreview() {
    const outputs = this.getDeployOutputs();
    return Boolean(outputs?.codeGs?.trim() && outputs?.pageHtml?.trim());
  }

  canCopy() {
    return this.hasCopyablePreview();
  }

  setReadonlyCode(target, value) {
    if (!target) return;
    target.value = value;
    target.classList.remove('portal-builder__code--placeholder');
    target.removeAttribute('aria-busy');
    const lineCount = (value.match(/\n/g) || []).length + 1;
    target.rows = Math.min(Math.max(lineCount, 8), 48);
  }

  updateCopyButtons() {
    const enabled = this.canCopy();
    const title = enabled
      ? 'Copy Code.gs or Page.html for Apps Script'
      : 'Complete headers, ID column, and page title to copy';

    if (this.hasCopyCodeBtnTarget) {
      this.copyCodeBtnTarget.disabled = !enabled;
      this.copyCodeBtnTarget.setAttribute('aria-disabled', String(!enabled));
      this.copyCodeBtnTarget.title = title;
    }
    if (this.hasCopyHtmlBtnTarget) {
      this.copyHtmlBtnTarget.disabled = !enabled;
      this.copyHtmlBtnTarget.setAttribute('aria-disabled', String(!enabled));
      this.copyHtmlBtnTarget.title = title;
    }

    if (this.hasOutputActionsTarget) {
      const copyAllBtn = this.outputActionsTarget.querySelector('[data-action*="copyAll"]');
      if (copyAllBtn) {
        copyAllBtn.disabled = !enabled;
        copyAllBtn.setAttribute('aria-disabled', String(!enabled));
        copyAllBtn.title = enabled ? 'Copy Code.gs and HTML' : 'Configure the portal to generate code first';
      }
    }
  }

  refreshLivePreview() {
    if (!this.hasCodeOutputTarget || !this.hasHtmlOutputTarget) return;

    this.config = this.configFromForm();

    try {
      const deployOutputs = generateDeployArtifacts(this.config);
      if (deployOutputs) {
        this.setReadonlyCode(this.codeOutputTarget, deployOutputs.codeGs);
        this.setReadonlyCode(this.htmlOutputTarget, deployOutputs.pageHtml);
        this.updateCodeWrapLayout();
        this.updateVisualPreview();
        this.updateCopyButtons();
        return;
      }

      const { config: previewConfig } = configForLivePreview(this.config);
      const code = generateCodeGs(previewConfig, { includeBuilderComments: false });
      const html = generatePageHtml(previewConfig);

      this.setReadonlyCode(this.codeOutputTarget, code);
      this.setReadonlyCode(this.htmlOutputTarget, html);
      this.updateCodeWrapLayout();
      this.updateVisualPreview(previewConfig);
      this.updateCopyButtons();
    } catch (err) {
      console.error('Portal builder live preview failed', err);
      this.showEmptyOutputs();
    }
  }

  updateVisualPreview(config = this.config) {
    if (!this.hasPagePreviewFrameTarget) return;

    try {
      const html = renderPagePreview(config);
      this.pagePreviewFrameTarget.srcdoc = html;
      if (this.hasPagePreviewEmptyTarget) this.pagePreviewEmptyTarget.hidden = true;
      if (this.hasPagePreviewWrapTarget) {
        this.pagePreviewWrapTarget.classList.add('portal-builder__preview-wrap--has-content');
      }
    } catch (err) {
      console.error('Portal page visual preview failed', err);
      this.clearVisualPreview();
    }
  }

  clearVisualPreview() {
    if (this.hasPagePreviewFrameTarget) this.pagePreviewFrameTarget.removeAttribute('srcdoc');
    if (this.hasPagePreviewEmptyTarget) this.pagePreviewEmptyTarget.hidden = false;
    if (this.hasPagePreviewWrapTarget) {
      this.pagePreviewWrapTarget.classList.remove('portal-builder__preview-wrap--has-content');
    }
  }

  readColumnsFromDom() {
    if (!this.hasColumnListTarget) return;
    const inputs = this.columnListTarget.querySelectorAll('[data-field="header"]');
    if (!inputs.length) {
      this.config.sheet.columns = [];
      return;
    }
    const ref = this.config.sheet.defaultReference === 'letter' ? 'letter' : 'header';
    this.config.sheet.columns = Array.from(inputs).map((input, i) => {
      const header = input.value.trim();
      return {
        header,
        key: headerToKey(header) || `column${i + 1}`,
        reference: ref,
        letter: indexToLetter(i),
        number: i + 1
      };
    });
  }

  configFromForm() {
    this.readColumnsFromDom();
    this.syncLookupFromSelect();
    const c = normalizeConfig(structuredClone(this.config));
    if (this.hasSheetNameTarget) c.sheet.name = this.sheetNameTarget.value.trim() || 'Sheet1';
    if (this.hasUrlParamTarget) c.sheet.urlParam = this.urlParamTarget.value.trim() || 'id';
    if (this.hasDefaultReferenceTarget) {
      const ref = this.defaultReferenceTarget.value === 'letter' ? 'letter' : 'header';
      c.sheet.defaultReference = ref;
      c.sheet.columns = c.sheet.columns.map((col) => ({ ...col, reference: ref }));
      if (c.sheet.lookup) c.sheet.lookup.reference = ref;
    }
    c.script.htmlFileName = 'Page';
    if (this.hasPageTitleTarget) c.script.pageTitle = this.pageTitleTarget.value.trim() || 'Portal';
    if (this.hasRenewalDaysTarget) c.script.renewalDays = parseInt(this.renewalDaysTarget.value, 10) || 14;
    c.script.allowAllFrames = true;

    c.html.sections.search = {
      enabled: this.hasSearchEnabledTarget ? this.searchEnabledTarget.checked : true,
      label: this.searchLabelTarget?.value || 'Search',
      placeholder: this.searchPlaceholderTarget?.value || '',
      buttonText: this.searchButtonTarget?.value || 'Search'
    };
    c.html.sections.intro = {
      ...c.html.sections.intro,
      enabled: this.hasIntroEnabledTarget
        ? this.introEnabledTarget.checked
        : Boolean(c.html.sections.intro?.enabled),
      body: this.hasIntroBodyTarget ? this.introBodyTarget.value : (c.html.sections.intro?.body || '')
    };
    c.html.sections.notFound = {
      message: this.notFoundMessageTarget?.value || 'Not found.'
    };
    c.html.sections.branding = {
      ...(c.html.sections.branding || {}),
      enabled: this.hasBrandEnabledTarget ? this.brandEnabledTarget.checked : false,
      logoUrl: this.brandLogoUrlTarget?.value?.trim() || '',
      companyName: this.brandCompanyNameTarget?.value?.trim() || '',
      description: this.brandDescriptionTarget?.value?.trim() || '',
      primaryColor: this.brandPrimaryColorTarget?.value || '#2563eb',
      backgroundColor: this.brandBackgroundColorTarget?.value || '#ffffff',
      cardColor: this.brandCardColorTarget?.value || '#e6e6e6',
      textColor: this.brandTextColorTarget?.value || '#334155'
    };

    if (c.features.doPostRenew) {
      c.html.sections.renewForm = {
        ...c.html.sections.renewForm,
        renewalDays: c.script.renewalDays
      };
    }

    this.applyPortalFieldsFromDom(c);
    return normalizeConfig(c);
  }

  namedColumnCount() {
    return (this.config.sheet.columns || []).filter((c) => String(c.header || '').trim()).length;
  }

  sheetStepRequirements() {
    const columns = this.config.sheet.columns || [];
    const hasHeaders = this.namedColumnCount() > 0;
    const hasLookup = this.hasLookupHeaderSelectTarget
      ? this.lookupHeaderSelectTarget.value !== ''
      : this.lookupColumnIndex() >= 0;

    const items = [
      {
        key: 'headers',
        done: hasHeaders,
        label: hasHeaders
          ? `${this.namedColumnCount()} column header${this.namedColumnCount() === 1 ? '' : 's'} added`
          : columns.length > 0
            ? 'Name at least one column header'
            : 'Paste or add row 1 headers',
        detail: hasHeaders
          ? null
          : columns.length > 0
            ? 'Type a name in any header cell, or paste row 1 from Google Sheets into the grid above.'
            : 'In Sheets: select row 1 → Copy. Click a cell in the header row below → Paste (or use + Column).'
      },
      {
        key: 'lookup',
        done: hasLookup,
        label: hasLookup ? 'Unique ID column selected' : 'Choose your unique ID column',
        detail: hasLookup
          ? null
          : hasHeaders
            ? 'Open the “Unique ID column” dropdown and pick the column each visitor uses to find their row (e.g. Order ID, Email).'
            : 'Available after you add at least one header.'
      }
    ];

    return {
      complete: hasHeaders && hasLookup,
      items
    };
  }

  sheetStepComplete() {
    return this.sheetStepRequirements().complete;
  }

  scriptStepComplete() {
    return Boolean(this.config.script?.pageTitle?.trim());
  }

  showEmptyOutputs() {
    if (this.hasCodeOutputTarget) {
      this.codeOutputTarget.value = '// Preview unavailable. Check your settings and try again.';
      this.codeOutputTarget.classList.add('portal-builder__code--placeholder');
    }
    if (this.hasHtmlOutputTarget) {
      this.htmlOutputTarget.value = '<!-- Preview unavailable -->';
      this.htmlOutputTarget.classList.add('portal-builder__code--placeholder');
    }
    this.clearVisualPreview();
    this.updateCodeWrapLayout();
    this.updateCopyButtons();
  }

  updateCodeWrapLayout() {
    if (this.hasCodeWrapTarget) {
      const hasCode = Boolean(this.codeOutputTarget?.value?.trim());
      this.codeWrapTarget.classList.toggle('portal-builder__code-wrap--has-code', hasCode);
    }
    if (this.hasHtmlWrapTarget) {
      const hasHtml = Boolean(this.htmlOutputTarget?.value?.trim());
      this.htmlWrapTarget.classList.toggle('portal-builder__code-wrap--has-code', hasHtml);
    }
  }

  setZoneState(el, state) {
    if (!el) return;
    el.classList.remove(
      'portal-builder__zone--active',
      'portal-builder__zone--complete',
      'portal-builder__zone--pending',
      'portal-builder__zone--preview'
    );
    if (state) el.classList.add(`portal-builder__zone--${state}`);
  }

  updateStepUi() {
    this.config = this.configFromForm();

    const sheetDone = this.sheetStepComplete();
    const portalDone = this.portalSettingsComplete();

    if (this.hasStepProgressTarget) {
      this.stepProgressTarget.textContent = portalDone
        ? 'Ready: copy and deploy'
        : 'Code and preview update on the right as you edit';
    }

    if (this.hasHtmlOutputTitleTarget) {
      this.htmlOutputTitleTarget.textContent = 'Page.html';
    }

    if (this.hasSheetPanelTarget) {
      this.setZoneState(this.sheetPanelTarget, sheetDone ? 'complete' : 'active');
    }

    const settingsPanel = this.hasSettingsPanelTarget
      ? this.settingsPanelTarget
      : this.hasPortalSettingsPanelTarget
        ? this.portalSettingsPanelTarget
        : null;

    if (settingsPanel) {
      this.setZoneState(
        settingsPanel,
        portalDone ? 'complete' : sheetDone ? 'active' : 'preview'
      );
    }

    this.setZoneState(this.codeOutputPanelTarget, 'preview');
    this.setZoneState(this.htmlOutputPanelTarget, 'preview');
    if (this.hasPagePreviewPanelTarget) {
      this.setZoneState(this.pagePreviewPanelTarget, 'preview');
    }
    const showDeployActions = portalDone;
    if (this.hasOutputActionsTarget) {
      this.outputActionsTarget.classList.toggle('hidden', !this.canCopy());
    }
    if (this.hasDeployActionsTarget) {
      this.deployActionsTarget.classList.toggle('hidden', !showDeployActions);
    }

    this.renderHints();
    this.updateStepNav();
    this.updateCopyButtons();
  }

  renderHints() {
    if (!this.hasColumnHintsTarget) return;
    const { messages, tone } = hintsForConfig(this.config);
    if (messages.length === 0) {
      this.columnHintsTarget.classList.add('hidden');
      this.columnHintsTarget.innerHTML = '';
      return;
    }
    this.columnHintsTarget.classList.remove('hidden');
    const toneClass =
      tone === 'warn' ? 'portal-builder__hint--warn' : tone === 'ok' ? 'portal-builder__hint--ok' : '';
    this.columnHintsTarget.className = `portal-builder__hint ${toneClass}`;
    this.columnHintsTarget.innerHTML = messages.map((m) => `<p>${m}</p>`).join('');
  }

  async copyCode() {
    if (!this.requireMembershipExport()) return;
    const outputs = this.getDeployOutputs();
    if (!outputs) {
      this.showToast('Add column headers, choose an ID column, and enter a page title before copying.');
      return;
    }
    await this.copyText(outputs.codeGs, 'Code.gs copied');
    try {
      localStorage.setItem(COPY_CODE_GS_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    this.updateStepNav();
  }

  async copyHtml() {
    if (!this.requireMembershipExport()) return;
    const outputs = this.getDeployOutputs();
    if (!outputs) {
      this.showToast('Add column headers, choose an ID column, and enter a page title before copying.');
      return;
    }
    await this.copyText(outputs.pageHtml, 'Page.html copied');
    try {
      localStorage.setItem(COPY_PAGE_HTML_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    this.updateStepNav();
  }

  async copyAll() {
    if (!this.requireMembershipExport()) return;

    const outputs = this.getDeployOutputs();
    if (!outputs) {
      this.showToast('Add column headers, choose an ID column, and enter a page title before copying.');
      return;
    }

    const bundle = `${outputs.codeGs}\n\n${outputs.pageHtml}`;

    try {
      await navigator.clipboard.writeText(bundle);
    } catch {
      this.showToast('Copy failed. Select output and copy manually.');
      return;
    }

    const recorded = await this.recordUse();
    if (recorded === false) return;

    const hadCopied = localStorage.getItem(COPY_STORAGE_KEY) === '1';
    localStorage.setItem(COPY_STORAGE_KEY, '1');

    this.showToast('Copied Code.gs and Page.html to clipboard.');
    if (hadCopied) {
      this.showToast('Changed code? Use Redeploy after edit (Manage deployments → New version).', 6000);
    } else {
      this.showToast('Next: open How to deploy in the header.', 6000);
      this.showDeployGuide('deploy');
    }
  }

  async recordUse() {
    if (!this.recordUseUrlValue) return true;

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(this.recordUseUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf || '',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: '{}'
      });

      if (response.status === 402) {
        this.paywall()?.handleLimitReached();
        return false;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.generator_usage) this.paywall()?.applyUsageFromResponse(data.generator_usage);
      }
      return true;
    } catch {
      return true;
    }
  }

  async copyText(text, message) {
    const trimmed = text?.trim() || '';
    if (
      !trimmed ||
      trimmed.startsWith(PREVIEW_UNAVAILABLE_CODE) ||
      trimmed.startsWith(PREVIEW_UNAVAILABLE_HTML)
    ) {
      this.showToast('Nothing to copy yet. Generate portal code first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      this.showToast(message);
    } catch {
      this.showToast('Copy failed.');
    }
  }

  showToast(message, timeout = 4000) {
    if (!this.hasToastTarget) return;
    this.toastTarget.textContent = message;
    this.toastTarget.classList.remove('hidden');
    clearTimeout(this.toastHideTimer);
    this.toastHideTimer = setTimeout(() => {
      this.toastTarget.classList.add('hidden');
    }, timeout);
  }

  openDeployGuide(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.hasCopyablePreview()) {
      this.showToast('Configure the portal first. Code and preview update on the right.');
      return;
    }
    const tab = event?.params?.tab || 'deploy';
    this.showDeployGuide(tab);
  }

  openRedeployGuide(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.hasCopyablePreview()) {
      this.showToast('Configure the portal first.');
      return;
    }
    this.showDeployGuide('redeploy');
  }

  showDeployGuide(tab) {
    if (!this.hasDeployGuideTarget) return;
    this.deployGuideTarget.removeAttribute('hidden');
    this.deployGuideTarget.setAttribute('aria-hidden', 'false');
    if (this.hasDeployBackdropTarget) {
      this.deployBackdropTarget.removeAttribute('hidden');
    }
    this.setDeployTab(tab);
    document.body.classList.add('portal-builder-guide-open');
  }

  closeDeployGuide(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.hasDeployGuideTarget) return;
    this.deployGuideTarget.setAttribute('hidden', '');
    this.deployGuideTarget.setAttribute('aria-hidden', 'true');
    if (this.hasDeployBackdropTarget) {
      this.deployBackdropTarget.setAttribute('hidden', '');
    }
    document.body.classList.remove('portal-builder-guide-open');
  }

  setDeployTab(tab) {
    const isRedeploy = tab === 'redeploy';
    if (this.hasDeployTabTarget) {
      this.deployTabTargets.forEach((el) => {
        const active = el.dataset.tab === tab;
        el.classList.toggle('portal-builder__tab--active', active);
        el.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }
    if (this.hasDeployPanelTarget) this.deployPanelTarget.hidden = isRedeploy;
    if (this.hasRedeployPanelTarget) this.redeployPanelTarget.hidden = !isRedeploy;
  }

  switchDeployTab(event) {
    event?.preventDefault();
    this.setDeployTab(event.params.tab);
  }
}
